import type { SearchableTransaction, TransactionFilters } from '../data/types';
import { inSet } from './shared';
import { matchesDimensionFilters } from './flow';
import { measured } from '../debug/telemetry';

function optionalBooleanMatches(value: boolean, expected: boolean | undefined): boolean {
  return expected === undefined || value === expected;
}

function filterTransactionsImpl(
  transactions: readonly SearchableTransaction[],
  filters: TransactionFilters,
): SearchableTransaction[] {
  if (filters.from > filters.to) throw new RangeError('Il periodo visibile è invertito.');

  return transactions.filter((transaction) => {
    if (transaction.date < filters.from || transaction.date > filters.to) return false;
    if (!matchesDimensionFilters(transaction, filters)) return false;
    if (!inSet(transaction.type, filters.types)) return false;
    if (
      filters.counterAccountIds !== undefined &&
      (transaction.counter_account_id === null ||
        !filters.counterAccountIds.has(transaction.counter_account_id))
    ) {
      return false;
    }
    if (
      filters.parentCategoryIds !== undefined &&
      ![
        transaction.parent_category_id,
        transaction.rollup_category_id,
        transaction.category_id,
      ].some((id) => id !== null && filters.parentCategoryIds?.has(id))
    ) {
      return false;
    }
    const absoluteAmount = Math.abs(transaction.amount_cents);
    if (filters.minAmountCents !== undefined && absoluteAmount < filters.minAmountCents)
      return false;
    if (filters.maxAmountCents !== undefined && absoluteAmount > filters.maxAmountCents)
      return false;
    if (filters.sign === 'positive' && transaction.amount_cents <= 0) return false;
    if (filters.sign === 'negative' && transaction.amount_cents >= 0) return false;
    if (!optionalBooleanMatches(Boolean(transaction.description?.trim()), filters.hasDescription))
      return false;
    if (!inSet(transaction.day_of_week, filters.daysOfWeek)) return false;
    if (!optionalBooleanMatches(transaction.is_weekend === 1, filters.weekend)) return false;
    if (filters.transactionId && !transaction.id.includes(filters.transactionId)) return false;
    if (filters.transferId && !(transaction.transfer_id ?? '').includes(filters.transferId))
      return false;
    return true;
  });
}

export const filterTransactions = measured('analytics.transactions', filterTransactionsImpl);
